import React, { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ImageIcon, Loader2Icon, MinusIcon, PlusIcon, ShoppingCartIcon, Trash2Icon, XIcon } from "lucide-react";
import { useCart } from "../contexts/CartContext";
import { formatPrice } from "../utils/productGroups";
import { createCheckout } from "../utils/squareWorker";
type CartDrawerProps = {
  onShopAll: () => void;
};
const ease = [0.23, 1, 0.32, 1] as const;
export function CartDrawer({
  onShopAll
}: CartDrawerProps) {
  const {
    items,
    itemCount,
    subtotalCents,
    currency,
    isOpen,
    closeCart,
    setItemQuantity,
    removeItem
  } = useCart();
  const reduceMotion = useReducedMotion();
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const startCheckout = async () => {
    if (isCheckingOut || items.length === 0) {
      return;
    }
    setIsCheckingOut(true);
    setCheckoutError(null);
    try {
      const {
        checkoutUrl
      } = await createCheckout(items.map(item => ({
        variationId: item.variationId,
        quantity: item.quantity
      })));
      window.location.assign(checkoutUrl);
      // Leave the button disabled while the browser navigates to Square.
    } catch (error: unknown) {
      setCheckoutError(error instanceof Error ? error.message : "Checkout failed. Please try again.");
      setIsCheckingOut(false);
    }
  };
  useEffect(() => {
    setCheckoutError(null);
  }, [items]);

  // If the customer comes back from Square with the browser's Back button, re-enable checkout.
  useEffect(() => {
    const handlePageShow = () => setIsCheckingOut(false);
    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, []);
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeCart();
      }
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKey);
    };
  }, [isOpen, closeCart]);
  const startShopping = () => {
    closeCart();
    onShopAll();
  };
  return <AnimatePresence data-id="element-92" data-mp="1fieog8y7i2co">
      {isOpen && <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label="Shopping cart" data-id="element-93" data-mp="1fhbxk10vvzwr">
          <motion.button type="button" aria-label="Close cart" onClick={closeCart} className="absolute inset-0 cursor-default bg-black/60" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease
      }} data-id="element-94" data-mp="1kf55ayhu51z5" />
          <motion.aside className="absolute inset-y-0 right-0 flex w-full max-w-md flex-col border-l border-[#ff3aa2]/40 bg-[#10070f] text-white" initial={reduceMotion ? {
        opacity: 0
      } : {
        x: "100%"
      }} animate={reduceMotion ? {
        opacity: 1
      } : {
        x: 0
      }} exit={reduceMotion ? {
        opacity: 0
      } : {
        x: "100%"
      }} transition={{
        duration: 0.26,
        ease
      }} data-id="element-95" data-mp="236o5yyoe03y0">
            <header className="flex items-center justify-between border-b border-white/10 px-5 py-4" data-id="element-96" data-mp="39qqtcmol0yq4">
              <h2 className="text-lg font-semibold text-[#ff5bad]" data-id="element-97" data-mp="b6s39p4pumme">
                Your cart{itemCount > 0 && <span className="ml-2 text-sm font-normal text-white/60" data-id="element-98" data-mp="6a1r3cw18b69">({itemCount} {itemCount === 1 ? "item" : "items"})</span>}
              </h2>
              <button type="button" onClick={closeCart} aria-label="Close cart" className="inline-flex h-9 w-9 items-center justify-center text-white/70 transition-colors duration-150 ease-out hover:text-[#35dceb] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#35dceb]" data-id="element-99" data-mp="1fxiyqb15x3l">
                <XIcon size={20} aria-hidden="true" data-id="element-100" data-mp="drgeuhoqaxju" />
              </button>
            </header>

            {items.length === 0 ? <div className="flex flex-1 flex-col items-center justify-center px-8 text-center" data-id="element-101" data-mp="3vy34443h24rb">
                <ShoppingCartIcon size={36} className="text-[#35dceb]" aria-hidden="true" data-id="element-102" data-mp="4rtabn0pdbmk" />
                <p className="mt-4 text-base font-medium" data-id="element-103" data-mp="sg2t0g2mdj9h">Your cart is empty</p>
                <p className="mt-1 text-sm text-white/60" data-id="element-104" data-mp="xf5wvx6v5jfv">Add a little sparkle to get started.</p>
                <button type="button" onClick={startShopping} className="mt-6 rounded-full bg-[#ff3aa2] px-6 py-3 text-sm font-semibold text-white transition-colors duration-150 ease-out hover:bg-[#ff5bad] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#35dceb]" data-id="element-105" data-mp="iqsxsrchbkh5">
                  Shop all
                </button>
              </div> : <>
                <ul className="flex-1 divide-y divide-white/10 overflow-y-auto px-5" data-id="element-106" data-mp="2vxhgj44poewi">
                  {items.map(item => {
              const atMax = item.quantity >= item.inventory;
              return <li key={item.variationId} data-square-variation-id={item.variationId} className="flex gap-4 py-4" data-id="element-107" data-mp="35o3qahpqyb7a">
                        {item.imageUrl ? <img src={item.imageUrl} alt="" className="h-20 w-20 shrink-0 bg-[#220d20] object-cover" data-id="element-108" data-mp="2cyddcbzupfcr" /> : <div className="flex h-20 w-20 shrink-0 items-center justify-center bg-[#220d20]" aria-hidden="true" data-id="element-109" data-mp="2qyxt0zagyv21">
                            <ImageIcon size={20} className="text-[#35dceb]" data-id="element-110" data-mp="2m163ud17ln1j" />
                          </div>}
                        <div className="min-w-0 flex-1" data-id="element-111" data-mp="19ca6n12jhts">
                          <div className="flex items-start justify-between gap-3" data-id="element-112" data-mp="1ax6ah3178tr">
                            <div className="min-w-0" data-id="element-113" data-mp="2v0al6quritow">
                              <p className="text-sm font-medium leading-5" data-id="element-114" data-mp="2zzdtck61hg13">{item.name}</p>
                              {item.variationName && <p className="mt-0.5 text-xs text-[#35dceb]" data-id="element-115" data-mp="3418unonyztev">{item.variationName}</p>}
                            </div>
                            <button type="button" onClick={() => removeItem(item.variationId)} aria-label={`Remove ${item.name} from cart`} className="shrink-0 text-white/50 transition-colors duration-150 ease-out hover:text-[#ff5bad] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#35dceb]" data-id="element-116" data-mp="38zshdrae198a">
                              <Trash2Icon size={16} aria-hidden="true" data-id="element-117" data-mp="1slsi8ijd9nm3" />
                            </button>
                          </div>
                          <div className="mt-3 flex items-center justify-between" data-id="element-118" data-mp="1q1vz7k0xxe4d">
                            <div className="flex items-center gap-2" data-id="element-119" data-mp="1pzji1930b7sd">
                              <button type="button" onClick={() => setItemQuantity(item.variationId, item.quantity - 1)} aria-label={`Remove one ${item.name}`} className="inline-flex h-7 w-7 items-center justify-center border border-white/30 transition-colors duration-150 ease-out hover:border-[#35dceb] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#35dceb]" data-id="element-120" data-mp="1l3c7f2zo1843">
                                <MinusIcon size={14} aria-hidden="true" data-id="element-121" data-mp="sf6nujkclp5u" />
                              </button>
                              <span className="min-w-6 text-center text-sm font-semibold" aria-live="polite" data-id="element-122" data-mp="ob8p49yxqgdb">
                                {item.quantity}
                              </span>
                              <button type="button" onClick={() => setItemQuantity(item.variationId, item.quantity + 1)} disabled={atMax} aria-label={`Add one ${item.name}`} className="inline-flex h-7 w-7 items-center justify-center border border-[#35dceb] text-[#35dceb] transition-colors duration-150 ease-out hover:bg-[#35dceb] hover:text-[#10070f] disabled:cursor-not-allowed disabled:opacity-35 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#35dceb]" data-id="element-123" data-mp="27bgc7qsi83tg">
                                <PlusIcon size={14} aria-hidden="true" data-id="element-124" data-mp="1jlcbbr6pai65" />
                              </button>
                            </div>
                            <p className="text-sm font-semibold text-[#ff5bad]" data-id="element-125" data-mp="1ajwaaa1rzsel">
                              {item.priceCents === null ? "—" : formatPrice(item.priceCents * item.quantity, item.currency)}
                            </p>
                          </div>
                          {atMax && <p className="mt-1.5 text-xs text-white/55" data-id="element-126" data-mp="m13zocup2694">Only {item.inventory} available</p>}
                        </div>
                      </li>;
            })}
                </ul>

                <footer className="border-t border-white/10 px-5 py-5" data-id="element-127" data-mp="19wyk3vn3i7fv">
                  <div className="flex items-baseline justify-between" data-id="element-128" data-mp="19v3w28gj1z7k">
                    <span className="text-sm text-white/75" data-id="element-129" data-mp="14x2g3weeeg0b">Subtotal</span>
                    <span className="text-lg font-semibold text-white" data-id="element-130" data-mp="1jn9spvhq54t3">{formatPrice(subtotalCents, currency)}</span>
                  </div>
                  <p className="mt-1 text-xs text-white/55" data-id="element-131" data-mp="1eo6scocovmrg">Shipping and taxes are calculated at checkout.</p>
                  {checkoutError && <p role="alert" className="mt-4 border border-[#ff5bad]/60 bg-[#3a1232] px-3 py-2 text-sm text-white" data-id="element-132" data-mp="1nomt3q21sfsm">
                      {checkoutError}
                    </p>}
                  <button type="button" onClick={startCheckout} disabled={isCheckingOut} aria-busy={isCheckingOut} className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#ff3aa2] px-6 py-3 text-sm font-semibold text-white transition-colors duration-150 ease-out hover:bg-[#ff5bad] disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#35dceb]" data-id="element-133" data-mp="2zp4f5vmz23b4">
                    {isCheckingOut && <Loader2Icon size={16} className="animate-spin" aria-hidden="true" data-id="element-134" data-mp="38ir7tcvvuqg3" />}
                    {isCheckingOut ? "Opening secure checkout…" : "Checkout"}
                  </button>
                  <p className="mt-2 text-center text-xs text-white/55" data-id="element-135" data-mp="1x2az02eu3827">You'll finish your purchase on Square's secure checkout.</p>
                </footer>
              </>}
          </motion.aside>
        </div>}
    </AnimatePresence>;
}